import React, { createContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import range from 'lodash/range';
import { useRouter } from 'next/router';
import useCallApiProcess from '@/hooks/useCallApiProcess';
import {
  useFindBestAvailableMutation,
  useReleaseGAObjectsMutation,
} from '@/store/seats-io/seats-io.api';
import {
  useTicketsSelected,
  useSeatsIO,
  useTicketTypes,
  useSaturated,
  useRsvp,
} from '@/store';
import {
  AlertDialog,
  ConfirmDialog,
  ConfirmCancelDialog,
  ConfirmQuantityDialog,
} from '@/components/dialogs';
import useEventContext from '@/contexts/event/useEventContext';
// import useAnalyticsEvents from '@/hooks/useAnalyticsEvents';
import ConfirmTriesDialog from '@/templates/plus/sections/saturated/ConfirmTriesDialog';
import { isNext } from '@/locales/dates';
import Cookies from 'js-cookie';
import useStepper from '@/hooks/checkout/useStepper';
import useUserTracking from '@/hooks/userTracking';

const initialState = {
  onRenderStarted: () => {},
  onTicketSelected: () => {},
  onTicketSelectedFromList: () => {},
  onDeleteTicket: () => {},
  onHoldTokenExpired: () => {},
  onObjectSelected: () => {},
  onObjectDeselected: () => {},
  onHoldSucceeded: () => {},
  onReleaseHoldSucceeded: () => {},
  onTicketTypeSelected: () => {},
  resetSeatsView: () => {},
  clearSelection: () => {},
  onObjectClicked: () => {},
  onObjectStatusChanged: () => {},
  onHoldFailed: () => {},
  onReleaseHoldFailed: () => {},
  validateTicketsInCart: () => {},
};

const TicketsContext = createContext(initialState);

function TicketsContextProvider({ children }) {
  const { t } = useTranslation();
  const { bestAvailable: bestAvailableLoading, releasingTickets } = t(
    'checkout.loadings',
    {
      returnObjects: true,
    },
  );
  const { onSaveTicketsFail, onBestAvailableFail } = t('checkout.modals', {
    returnObjects: true,
  });
  const router = useRouter();
  const [seatsIoChart, setSeatsIoChart] = useState();
  const [ticketSelected, setTicketSelected] = useState();
  const [isCallBackAction, setIsCallBackAction] = useState(false);
  const {
    addSeatsIOTicket,
    removeSeatsIOTicket,
    addTicketTypeQuantity,
    removeTicketType,
    hasTickets,
    removeAllTickets,
    removeTicket,
    ticketsSelected,
  } = useTicketsSelected();
  const { categories, getByTicketTypeID } = useTicketTypes();

  // const {
  //   ticketSelectedFromList,
  //   clickOnDeleteTicketFromList,
  //   cancelDeleteTicketFromList,
  //   ticketRemovedFromList,
  //   ticketSelectedFromMap,
  //   ticketRemovedFromMap,
  //   addTicketTypeQuantity: addTicketTypeQuantityEvent,
  //   removeTicketType: removeTicketTypeEvent,
  //   clickedOnMap,
  // } = useAnalyticsEvents();
  const { isEnabled, tries, decreaseTriesCounter } = useSaturated();

  const { mapSeatSelected } = useUserTracking();

  const [callBestAvailable] = useFindBestAvailableMutation();
  const {
    callApi: callApiBestAvailable,
    openModal,
    closeModal,
  } = useCallApiProcess(callBestAvailable, bestAvailableLoading, 3000, {
    message: onBestAvailableFail.message,
    title: onBestAvailableFail.title,
    label: onBestAvailableFail.label,
  });

  const [callReleaseGAObjects] = useReleaseGAObjectsMutation();
  const { callApi: callApiReleaseGAObjects, showBackdrop } = useCallApiProcess(
    callReleaseGAObjects,
    releasingTickets,
  );

  const {
    seatsIO: { holdToken, expiresAt },
  } = useSeatsIO();

  const {
    event: {
      event: { seats_event_key, use_seats },
    },
  } = useEventContext();

  const { isActive: isActiveRsvp, rsvp } = useRsvp();

  const selectSeatsIOTicket = (object, selectedTicketType) => {
    const seat = addSeatsIOTicket(object, selectedTicketType);
    let ticketType;
    if (object.objectType === 'GeneralAdmissionArea') {
      ticketType = getByTicketTypeID(seat[0].ticket_type_id);
    } else {
      ticketType = getByTicketTypeID(seat.ticket_type_id);
    }
    setTicketSelected(ticketType);
  };

  const validateTicketsInCart = async () => {
    try {
      if (use_seats) {
        if (isNext(expiresAt)) {
          const ticketsOnSeatsIO = await seatsIoChart.listSelectedObjects();
          let missingTickets = [];
          ticketsSelected.map((ticket) => {
            const seat = ticketsOnSeatsIO.find(
              (item) => item.id === ticket.seatsId,
            );
            if (!seat) {
              missingTickets = [...missingTickets, ticket];
            }
            if (seat.objectType === 'GeneralAdmissionArea') {
              if (
                Object.keys(seat.selectionPerTicketType).length > 0 &&
                seat.selectionPerTicketType[ticket.ticket_type_id] !==
                  ticket.quantity
              ) {
                missingTickets = [...missingTickets, ticket];
              } else if (
                Object.keys(seat.selectionPerTicketType).length < 1 &&
                seat.numSelected !== ticket.quantity
              ) {
                missingTickets = [...missingTickets, ticket];
              }
            }
          });

          if (missingTickets.length > 0) {
            await seatsIoChart.clearSelection();
            removeAllTickets();
            return onTicketsNoLongerAvailable();
          }
        } else {
          await seatsIoChart.clearSelection();
          removeAllTickets();
          return holdTokenExpired();
        }
      }
    } catch (e) {
      router.reload(window.location.pathname);
    }
    return true;
  };

  /** Seats IO Chart Events */

  const onRenderStarted = (chart) => {
    setSeatsIoChart(chart);
  };

  const onObjectClicked = (args) => {
    // clickedOnMap();
  };

  const onHoldTokenExpired = () => {
    const { title, text, acceptLabel } = t(
      'checkout.modals.onHoldTokenExpired',
      {
        returnObjects: true,
      },
    );
    openModal(AlertDialog, {
      title,
      text,
      acceptLabel,
      blockClose: true,
      onConfirm: () => {
        router.reload();
      },
    });
  };

  const onMaxTickets = (max) => {
    const { title, text, acceptLabel } = t('checkout.modals.onMaxTickets', {
      returnObjects: true,
      max,
    });
    return new Promise((resolve) => {
      openModal(AlertDialog, {
        title,
        text,
        acceptLabel,
        blockClose: true,
        onConfirm: async () => {
          resolve();
        },
      });
    });
  };

  const onTicketsNoLongerAvailable = (max) => {
    return new Promise((resolve) => {
      openModal(AlertDialog, {
        title: onSaveTicketsFail.title,
        text: onSaveTicketsFail.message,
        acceptLabel: onSaveTicketsFail.label,
        blockClose: true,
        onConfirm: async () => {
          router.reload();
          resolve(false);
        },
      });
    });
  };

  const holdTokenExpired = (max) => {
    const { title, text, acceptLabel } = t(
      'checkout.modals.onHoldTokenExpired',
      {
        returnObjects: true,
      },
    );
    return new Promise((resolve) => {
      openModal(AlertDialog, {
        title: title,
        text: text,
        acceptLabel,
        blockClose: true,
        onConfirm: async () => {
          resolve(false);
        },
      });
    });
  };

  const onObjectStatusChanged = (object) => {
    // console.log('onObjectStatusChanged', object);
  };

  const onHoldFailed = (object, selectedTicketType) => {
    console.log('onHoldFailed', object, selectedTicketType);
  };

  const onReleaseHoldFailed = (object, selectedTicketType) => {
    console.log('onReleaseHoldFailed', object, selectedTicketType);
  };

  const onObjectSelected = (object, selectedTicketType) => {
    mapSeatSelected({
      zoneId: object?.category?.key,
      seatId: object?.seatId,
      zoneName: object?.category?.label,
      seatNumber: object?.labels?.own,
    });
    selectSeatsIOTicket(object, selectedTicketType);
    // ticketSelectedFromMap(object);
  };

  const onObjectDeselected = (object, deselectedTicketType) => {
    // console.log('onObjectDeselected', object, deselectedTicketType);
    if (object.status !== 'booked') {
      if (object.objectType !== 'GeneralAdmissionArea') {
        removeSeatsIOTicket(object);
        // ticketRemovedFromMap(object);
      }
    } else {
      if (isActiveRsvp && rsvp.type === 'hold') {
        removeSeatsIOTicket(object);
        // ticketRemovedFromMap(object);
      }
    }
  };

  const onHoldSucceeded = (object, selectedTicketType) => {
    // console.log('onHoldSucceeded', object, selectedTicketType);
  };

  const onReleaseHoldSucceeded = (arrObj) => {
    const object = arrObj[0];
    // console.log('onReleaseHoldSucceeded', object);
    if (object.objectType === 'GeneralAdmissionArea') {
      removeSeatsIOTicket(object);
      // ticketRemovedFromMap(object);
    }
  };

  /** End Seats IO Chart Events */

  const onTicketSelectedRSVP = async (ticket, quantity) => {
    const rsvpTicketTypes = rsvp.detail.filter((t) => {
      return t.tickets.includes(ticket.ticket_type_id);
    });

    if (rsvpTicketTypes && rsvpTicketTypes.length > 0) {
      if (quantity > rsvpTicketTypes.length) {
        return await checkAvailabilityRSVP(
          ticket,
          rsvpTicketTypes.length,
          rsvpTicketTypes,
        );
      } else {
        return await bestAvailableFromRSVP(ticket, quantity, rsvpTicketTypes);
      }
    }
  };
  const bestAvailableFromRSVP = async (ticket, quantity, rsvpTicketTypes) => {
    const ticketsRSVPSelected = rsvpTicketTypes
      .slice(0, quantity)
      .map(
        ({ section, row, seat }) =>
          `${section}${row ? '-' : ''}${row}${seat ? '-' : ''}${seat}`,
      );

    seatsIoChart.selectObjects(ticketsRSVPSelected);
    setTicket(ticket);
    return true;
  };

  const checkAvailabilityRSVP = (ticket, availability, rsvpTicketTypes) => {
    const { title, text, acceptLabel, cancelLabel } = t(
      'checkout.modals.checkAvailabilityRSVP',
      {
        returnObjects: true,
        availability,
      },
    );
    return new Promise((resolve) => {
      openModal(ConfirmCancelDialog, {
        title,
        text,
        acceptLabel,
        cancelLabel,
        onConfirm: async () => {
          const resp = await bestAvailableFromRSVP(
            ticket,
            availability,
            rsvpTicketTypes,
          );
          resolve(resp);
        },
        onCancel: async () => {
          closeModal();
          resolve(false);
        },
      });
    });
  };

  const bestAvailable = async (ticket, quantity) => {
    const category = categories.find(
      (item) => item.category === ticket.category_id,
    );
    const resp = await callApiBestAvailable({
      holdToken,
      seatsEvent: seats_event_key,
      quantity,
      categories: [ticket.category_name],
      ...(category.ticketTypes
        ? { ticketTypes: range(quantity).map(() => `${ticket.ticket_type_id}`) }
        : {}),
    });

    if (resp.data?.objects) {
      // ticketSelectedFromList(quantity, ticket);
      setTicket(ticket);
      // seatsIoChart.selectObjects(resp.data.objects);
      return true;
    }
    return false;
  };

  const checkAvailability = (ticket, availability) => {
    const { title, text, acceptLabel, cancelLabel } = t(
      'checkout.modals.checkAvailability',
      {
        returnObjects: true,
        availability,
      },
    );
    return new Promise((resolve) => {
      openModal(ConfirmCancelDialog, {
        title,
        text,
        acceptLabel,
        cancelLabel,
        onConfirm: async () => {
          const resp = await bestAvailable(ticket, availability);
          resolve(resp);
        },
        onCancel: async () => {
          closeModal();
          resolve(false);
        },
      });
    });
  };

  const checkMinPerSale = (ticket, minPerSel) => {
    const { title, text, acceptLabel, cancelLabel } = t(
      'checkout.modals.checkMinPerSale',
      {
        returnObjects: true,
        minPerSel,
      },
    );
    return new Promise((resolve) => {
      openModal(ConfirmCancelDialog, {
        title,
        text,
        acceptLabel,
        cancelLabel,
        onConfirm: async () => {
          const resp = await bestAvailable(ticket, minPerSel);
          resolve(resp);
        },
        onCancel: async () => {
          closeModal();
          resolve(false);
        },
      });
    });
  };

  const changeQuantity = (ticket, quantity) =>
    new Promise((resolve) => {
      openModal(ConfirmQuantityDialog, {
        quantity,
        onConfirm: async (quantity) => {
          const resp = await bestAvailable(ticket, quantity);
          resolve(resp);
        },
        onCancel: async () => {
          closeModal();
          resolve(false);
        },
      });
    });

  const onTicketSelected = async (currentTicket) => {
    const { ticket, quantity } = currentTicket;
    const { availability, max_per_sale, min_per_sale } = ticket;
    if (!hasTickets) {
      if (isActiveRsvp && rsvp.type === 'hold') {
        return onTicketSelectedRSVP(ticket, quantity);
      }
      if (seats_event_key && seatsIoChart) {
        if (quantity > max_per_sale) {
          return await onMaxTickets(ticket.max_per_sale);
        }
        if (availability < quantity) {
          if (isEnabled) {
            return await changeQuantity(ticket, quantity);
          } else {
            return await checkAvailability(ticket, availability);
          }
        } else {
          if (quantity < min_per_sale) {
            return await bestAvailable(ticket, min_per_sale);
          } else {
            return await bestAvailable(ticket, quantity);
          }
        }
      }
    } else {
      setTicket(ticket);
    }
    return true;
  };
  const onTicketSelectedFromList = async (currentTicket) => {
    const { ticket, quantity } = currentTicket;
    const { availability } = ticket;
    if (seats_event_key && seatsIoChart) {
      if (quantity > ticket.max_per_sale) {
        return await onMaxTickets(ticket.max_per_sale);
      }
      if (availability < quantity) {
        if (isEnabled) {
          return await changeQuantity(ticket, quantity);
        } else {
          return await checkAvailability(ticket, availability);
        }
      } else {
        return await bestAvailable(ticket, quantity);
      }
    }
    return true;
  };

  const onTicketTypeSelected = ({ ticket, quantity }) => {
    if (quantity > 0) {
      addTicketTypeQuantity(ticket, quantity);
      // addTicketTypeQuantityEvent(quantity, ticket);
    } else {
      removeTicketType(ticket);
      // removeTicketTypeEvent(ticket);
    }
  };

  const onDeleteTicket = async (ticket) => {
    // clickOnDeleteTicketFromList(ticket);
    const { title, text, acceptLabel } = t('checkout.modals.onDeleteTicket', {
      returnObjects: true,
    });
    openModal(ConfirmDialog, {
      title,
      text,
      acceptLabel,
      onConfirm: async () => {
        if (ticket.objectType === 'GeneralAdmissionArea') {
          const resp = await callApiReleaseGAObjects({
            objectId: ticket.seatsId,
            holdToken,
            seatsEvent: seats_event_key,
            quantity: ticket.quantity,
          });
          if (!resp.error) {
            removeTicket(ticket.id);
          }
        } else {
          seatsIoChart.deselectObjects([ticket.id]);
        }
        // ticketRemovedFromList(ticket);
      },
      onClose: () => {
        // cancelDeleteTicketFromList(ticket);
      },
    });
  };

  const confirmTry = () =>
    new Promise((resolve) => {
      openModal(ConfirmTriesDialog, {
        tries,
        onConfirm: async () => {
          showBackdrop();
          await seatsIoChart.clearSelection();
          removeAllTickets();
          closeModal();
          decreaseTriesCounter();
          resolve(true);
        },
        onClose: async () => {
          resolve(false);
        },
      });
    });

  const setTicket = (ticket) => {
    setTicketSelected(ticket);
    seatsIoChart.changeConfig({ filteredCategories: [ticket.category_name] });
    seatsIoChart.zoomToFilteredCategories();
  };

  const resetSeatsView = () => {
    if (seatsIoChart) {
      seatsIoChart.resetView();
    }
  };

  const callBackAction = async (callBack = () => {}) => {
    if (isEnabled) {
      setIsCallBackAction(true);
      const resp = await confirmTry();
      setIsCallBackAction(false);
      if (resp) {
        callBack();
        resetSeatsView();
      }
    } else {
      callBack();
      resetSeatsView();
    }
  };

  const clearSelection = () => {
    const { title, text, acceptLabel } = t('checkout.modals.clearSelection', {
      returnObjects: true,
    });
    return new Promise((resolve) => {
      openModal(ConfirmDialog, {
        title,
        text,
        acceptLabel,
        onConfirm: async () => {
          await deleteTickets();
          resolve(true);
        },
        onClose: async () => {
          closeModal();
          resolve(false);
        },
      });
    });
  };

  const deleteTickets = async () => {
    showBackdrop();
    await seatsIoChart.clearSelection();
    removeAllTickets();
    closeModal();
  };

  return (
    <TicketsContext.Provider
      value={{
        seatsIoChart,
        ticketSelected,
        isCallBackAction,
        onRenderStarted,
        onHoldTokenExpired,
        onObjectSelected,
        onObjectDeselected,
        onHoldSucceeded,
        onReleaseHoldSucceeded,
        onTicketSelected,
        onTicketSelectedFromList,
        onDeleteTicket,
        onTicketTypeSelected,
        callBackAction,
        clearSelection,
        onObjectClicked,
        onObjectStatusChanged,
        onHoldFailed,
        onReleaseHoldFailed,
        validateTicketsInCart,
      }}
    >
      {children}
    </TicketsContext.Provider>
  );
}

export { TicketsContextProvider, TicketsContext };
